// A trigger and the popover anchored beside it (not portaled): a mouse opens
// it by hovering, a click or tap opens it pinned, and it closes on a click of
// the trigger, Escape, a pointer down elsewhere, or the mouse leaving while
// it is not pinned.
import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

const CLOSE_DELAY_MS = 80;

interface HoverPopoverHandlers {
  onPointerEnter: (e: ReactPointerEvent) => void;
  onPointerLeave: (e: ReactPointerEvent) => void;
}

interface UseHoverPopoverResult {
  /** True while the popover is shown: hovered, or pinned open. */
  open: boolean;
  /** Attach to the wrapper that contains both the trigger and the panel —
   *  the popover isn't portaled, so this one element's pointer boundary
   *  covers both as a single continuous hoverable region. */
  wrapRef: React.RefObject<HTMLDivElement>;
  /** Wire to the trigger's onClick: closes an open popover, however it was
   *  opened, and pins a closed one open. */
  toggleTap: () => void;
  /** Closes it, however it was opened. */
  close: () => void;
  /** Spread onto the same wrapper `wrapRef` is attached to. */
  pointerHandlers: HoverPopoverHandlers;
}

export function useHoverPopover(): UseHoverPopoverResult {
  const [hover, setHover] = useState(false);
  const [tapOpen, setTapOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const wrapRef = useRef<HTMLDivElement>(null);
  const open = hover || tapOpen;

  function close() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHover(false);
    setTapOpen(false);
  }

  // A pinned popover closes on a pointer down anywhere outside it.
  useEffect(() => {
    if (!tapOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setTapOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [tapOpen]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Hover is a mouse's alone: tapping a trigger fires a pointerenter with no
  // matching pointerleave, which would leave the popover stuck open.
  function onPointerEnter(e: ReactPointerEvent) {
    if (e.pointerType !== "mouse") return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHover(true);
  }
  function onPointerLeave(e: ReactPointerEvent) {
    if (e.pointerType !== "mouse") return;
    closeTimer.current = setTimeout(() => setHover(false), CLOSE_DELAY_MS);
  }

  return {
    open,
    wrapRef,
    toggleTap: () => (open ? close() : setTapOpen(true)),
    close,
    pointerHandlers: { onPointerEnter, onPointerLeave },
  };
}
